import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Section, Eyebrow, Lede } from "../components/site/Section";
import Placeholder from "../components/site/Placeholder";
import { CHARACTERS, NATIONS } from "../lib/content";
import {
  getUnlocked, getArchiveStats,
  NATIONS_LIST, NATION_BY_SLUG, RARITY_STYLES,
} from "../lib/archive";
import { useAuth, getMyCast } from "../lib/auth";

// Display label used on cards (matches existing CHARACTERS shape: e.g. "Verdani", "Akirans").
// Maps each archive nation slug to the label already used in `CHARACTERS`.
function nationLabel(slug) {
  if (slug === "akira") return "Akirans";
  const n = NATION_BY_SLUG[slug];
  return n ? n.name : slug;
}

// Build a unified array of canonical + unlocked archive characters.
function mergeCharacters(unlocked) {
  const archiveCards = unlocked.map((u) => ({
    slug: `archive-${u.id}`,
    name: u.name,
    epithet: u.title || "Archive Record",
    nation: nationLabel(u.nation),
    role: "Recovered from the Archive",
    line: u.quote || "",
    bio: u.bio || "",
    power: u.abilities || "",
    image: u.image || "",
    imageObjectPosition: u.image_position || "center",
    accent: NATION_BY_SLUG[u.nation]?.accent || "#C5A059",
    rarity: u.rarity || "C",
    isArchive: true,
  }));
  return [...CHARACTERS, ...archiveCards];
}

export default function CharactersPage() {
  const { user, loading: authLoading } = useAuth();
  const [filter, setFilter] = useState("All");
  const [unlocked, setUnlocked] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    // Wait for auth to resolve so we know which source to pull from.
    if (authLoading) return;
    (async () => {
      try {
        // Logged-in: show characters THIS user has personally unlocked.
        // Logged-out: fall back to the public global wall.
        const charsPromise = user
          ? getMyCast().then((r) => r?.characters || [])
          : getUnlocked();
        const [u, s] = await Promise.all([charsPromise, getArchiveStats()]);
        setUnlocked(u || []);
        setStats(s || null);
      } catch {
        /* archive optional */
      }
    })();
  }, [user, authLoading]);

  const merged = useMemo(() => mergeCharacters(unlocked), [unlocked]);
  const FILTERS = useMemo(
    () => ["All", ...Array.from(new Set(merged.map((c) => c.nation)))],
    [merged]
  );

  const filtered = useMemo(() => {
    if (filter === "All") return merged;
    return merged.filter((c) => c.nation === filter);
  }, [filter, merged]);

  // Stats counter shown alongside the filter selection.
  const counter = useMemo(() => {
    if (!stats) return null;
    if (filter === "All") {
      return { unlocked: stats.total_unlocked, total: stats.total };
    }
    // Reverse-lookup archive slug from display label
    const slug = NATIONS_LIST.find((n) => nationLabel(n.slug) === filter)?.slug;
    if (!slug) return null;
    const s = stats.nations[slug];
    return s ? { unlocked: s.unlocked, total: s.total } : null;
  }, [filter, stats]);

  return (
    <div data-testid="characters-page">
      <Section testId="characters-hero" className="pt-12 md:pt-24">
        <Eyebrow>The Cast</Eyebrow>
        <h1
          data-testid="characters-title"
          className="font-display text-4xl sm:text-5xl md:text-7xl text-[#EAE0D5] leading-[1] mt-4"
        >
          The Bound, the Broken,
          <br />
          <span className="text-[#C5A059] italic font-light">the Becoming</span>
        </h1>
        <Lede>
          A trio of unlikely survivors at the heart of Season One — and the
          antagonists who force them to grow. Each character carries the mark
          of their homeland into a world that refuses them peace.
        </Lede>

        <div className="mt-10 flex flex-wrap gap-2 items-center" data-testid="character-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              data-testid={`character-filter-${f.toLowerCase()}`}
              className={`text-[10px] tracking-[0.24em] uppercase px-4 py-2 border transition-colors ${
                filter === f
                  ? "border-[#C5A059] text-black bg-[#C5A059]"
                  : "border-white/10 text-[#A39E99] hover:text-[#EAE0D5] hover:border-[#C5A059]/40"
              }`}
            >
              {f}
            </button>
          ))}

          {counter && (
            <Link
              to="/archive"
              data-testid="archive-counter"
              className="ml-auto text-[10px] tracking-[0.24em] uppercase border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 px-4 py-2 inline-flex items-center gap-2"
              title="Open the Bakhtak Archive"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059]" />
              Archive Unlocked {counter.unlocked} / {counter.total}
            </Link>
          )}
        </div>
      </Section>

      <Section testId="characters-grid" className="pt-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8">
          {filtered.map((c) => {
            const rstyle = c.isArchive ? RARITY_STYLES[c.rarity] : null;
            return (
              <article
                key={c.slug}
                data-testid={`character-card-${c.slug}`}
                className="bg-[#0A0A0A] border border-white/5 hover:border-[#C5A059]/30 transition-all duration-500 group flex flex-col"
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = `0 0 60px -25px ${c.accent}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "0 0 0 transparent";
                }}
              >
                <div className="relative">
                  <Placeholder
                    src={c.image}
                    ratio="aspect-[3/4]"
                    label={c.name}
                    alt={`${c.name} portrait`}
                    objectPosition={c.imageObjectPosition}
                  />
                  {rstyle && (
                    <div
                      className="absolute top-3 left-3 inline-flex items-center gap-1.5 border px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase backdrop-blur-sm bg-black/50"
                      style={{ borderColor: `${rstyle.color}66`, color: rstyle.color }}
                      data-testid={`card-rarity-${c.rarity}`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: rstyle.color }} />
                      {rstyle.label}
                    </div>
                  )}
                  {c.isArchive && (
                    <div className="absolute top-3 right-3 text-[10px] tracking-[0.22em] uppercase px-2 py-1 bg-black/60 text-[#C5A059] backdrop-blur-sm border border-[#C5A059]/30">
                      Archive
                    </div>
                  )}
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center justify-between">
                    <div
                      className="text-[10px] tracking-[0.28em] uppercase"
                      style={{ color: c.accent }}
                    >
                      {c.epithet}
                    </div>
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#737373]">
                      {c.nation}
                    </div>
                  </div>
                  <h3 className="font-display text-2xl text-[#EAE0D5] mt-3">
                    {c.name}
                  </h3>
                  <div className="text-xs text-[#737373] mt-1">{c.role}</div>
                  {c.line && (
                    <p className="italic text-[#A39E99] mt-4 leading-relaxed text-sm">
                      "{c.line}"
                    </p>
                  )}
                  {c.bio && (
                    <p className="text-sm text-[#A39E99] mt-4 leading-relaxed flex-1">
                      {c.bio}
                    </p>
                  )}
                  {c.power && (
                    <>
                      <div
                        className="text-[10px] tracking-[0.22em] uppercase mt-5 pt-4 border-t border-white/5"
                        style={{ color: c.accent }}
                      >
                        {c.isArchive ? "Abilities" : "Power"}
                      </div>
                      <div className="text-xs text-[#A39E99] mt-1.5">{c.power}</div>
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div
            className="border border-dashed border-white/10 p-12 text-center"
            data-testid="characters-empty"
          >
            <p className="text-[#A39E99]">No characters match this filter.</p>
          </div>
        )}

        {/* Archive teaser block */}
        <div
          className="mt-16 md:mt-24 border border-[#C5A059]/25 bg-[#0A0A0A] p-7 md:p-10 grid md:grid-cols-12 gap-6 items-center"
          data-testid="cast-archive-cta"
        >
          <div className="md:col-span-8">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C5A059]">The Bakhtak Archive</div>
            <h3 className="font-display text-2xl md:text-3xl text-[#EAE0D5] mt-2">
              More records are sealed in the vault.
            </h3>
            <p className="text-sm md:text-base text-[#A39E99] mt-3 max-w-xl leading-relaxed">
              Each week, one figure surfaces. Identify their nation correctly and they
              join this Cast — permanently, for everyone.
              {stats && (
                <span className="text-[#EAE0D5]">
                  {" "}Currently <strong>{stats.total_unlocked}</strong> of <strong>{stats.total}</strong> recovered.
                </span>
              )}
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/archive"
              data-testid="cast-to-archive-link"
              className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-6 py-3 text-xs tracking-[0.24em] uppercase hover:bg-[#D4AF68]"
            >
              Enter the Archive →
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
